import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  Button,
  Skeleton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  FreeBreakfast,
  LunchDining,
  DinnerDining,
  Icecream,
  LocalDining,
  Tapas,
  Public,
  Flag,
  Kitchen,
  Grass,
  Apple,
  Grain,
  HealthAndSafety,
  Whatshot,
  Star,
  ExpandMore,
  ArrowForward,
  Check as CheckIcon,
  Clear as ClearIcon,
  LocalCafe,
} from '@mui/icons-material';
import { useRecipe } from '../context/RecipeContext';
import { useAuth } from '../hooks/useAuth';
import { mockGenerateRecipes } from '../utils/MockAPI';

const categories = [
  {
    name: 'mealType',
    title: 'Meal Type',
    required: true,
    chips: [
      { label: 'Breakfast', icon: <FreeBreakfast /> },
      { label: 'Brunch', icon: <LocalCafe /> },
      { label: 'Lunch', icon: <LunchDining /> },
      { label: 'Dinner', icon: <DinnerDining /> },
      { label: 'Snack', icon: <Icecream /> },
      { label: 'Appetizer', icon: <Tapas /> },
      { label: 'Main Course', icon: <LocalDining /> },
    ],
  },
  {
    name: 'cuisineStyle',
    title: 'Cuisine Style',
    required: false,
    chips: [
      { label: 'Italian', icon: <Public /> },
      { label: 'Mexican', icon: <Flag /> },
      { label: 'Chinese', icon: <Public /> },
      { label: 'Indian', icon: <Public /> },
      { label: 'French', icon: <Public /> },
      { label: 'Japanese', icon: <Public /> },
      { label: 'Thai', icon: <Public /> },
      { label: 'American', icon: <Public /> },
      { label: 'Korean', icon: <Public /> },
      { label: 'Vietnamese', icon: <Public /> },
      { label: 'Greek', icon: <Public /> },
      { label: 'Spanish', icon: <Public /> },
      { label: 'Brazilian', icon: <Public /> },
      { label: 'Moroccan', icon: <Public /> },
      { label: 'Lebanese', icon: <Public /> },
      { label: 'Turkish', icon: <Public /> },
      { label: 'Caribbean', icon: <Public /> },
    ],
  },
  {
    name: 'mainIngredients',
    title: 'Main Ingredients',
    required: true,
    chips: [
      { label: 'Chicken', icon: <Kitchen /> },
      { label: 'Beef', icon: <Kitchen /> },
      { label: 'Pork', icon: <Kitchen /> },
      { label: 'Fish', icon: <Kitchen /> },
      { label: 'Tofu', icon: <Kitchen /> },
      { label: 'Lentils', icon: <Kitchen /> },
      { label: 'Eggs', icon: <Kitchen /> },
      { label: 'Cheese', icon: <Kitchen /> },
      { label: 'Turkey', icon: <Kitchen /> },
      { label: 'Lamb', icon: <Kitchen /> },
      { label: 'Shrimp', icon: <Kitchen /> },
      { label: 'Salmon', icon: <Kitchen /> },
      { label: 'Ground Beef', icon: <Kitchen /> },
      { label: 'Sausage', icon: <Kitchen /> },
      { label: 'Bacon', icon: <Kitchen /> },
      { label: 'Duck', icon: <Kitchen /> },
      { label: 'Beans', icon: <Kitchen /> },
      { label: 'Chickpeas', icon: <Kitchen /> },
    ],
  },
  {
    name: 'vegetablesFruits',
    title: 'Vegetables & Fruits',
    required: true,
    chips: [
      { label: 'Tomato', icon: <Grass /> },
      { label: 'Onion', icon: <Grass /> },
      { label: 'Carrot', icon: <Grass /> },
      { label: 'Spinach', icon: <Grass /> },
      { label: 'Apple', icon: <Apple /> },
      { label: 'Banana', icon: <Apple /> },
      { label: 'Orange', icon: <Apple /> },
      { label: 'Broccoli', icon: <Grass /> },
      { label: 'Potato', icon: <Grass /> },
      { label: 'Garlic', icon: <Grass /> },
      { label: 'Lettuce', icon: <Grass /> },
      { label: 'Cucumber', icon: <Grass /> },
      { label: 'Bell Pepper', icon: <Grass /> },
      { label: 'Strawberry', icon: <Apple /> },
      { label: 'Blueberry', icon: <Apple /> },
      { label: 'Lemon', icon: <Apple /> },
      { label: 'Avocado', icon: <Apple /> },
      { label: 'Mushroom', icon: <Grass /> },
      { label: 'Zucchini', icon: <Grass /> },
      { label: 'Pineapple', icon: <Apple /> },
      { label: 'Grapes', icon: <Apple /> },
      { label: 'Cauliflower', icon: <Grass /> },
      { label: 'Eggplant', icon: <Grass /> },
      { label: 'Kale', icon: <Grass /> },
      { label: 'Celery', icon: <Grass /> },
      { label: 'Green Beans', icon: <Grass /> },
      { label: 'Corn', icon: <Grass /> },
      { label: 'Sweet Potato', icon: <Grass /> },
      { label: 'Mango', icon: <Apple /> },
      { label: 'Peach', icon: <Apple /> },
      { label: 'Pear', icon: <Apple /> },
      { label: 'Lime', icon: <Apple /> },
      { label: 'Cherry', icon: <Apple /> },
    ],
  },
  {
    name: 'grainsStaples',
    title: 'Grains & Staples',
    required: false,
    chips: [
      { label: 'Rice', icon: <Grain /> },
      { label: 'Pasta', icon: <Grain /> },
      { label: 'Bread', icon: <Grain /> },
      { label: 'Quinoa', icon: <Grain /> },
      { label: 'Oats', icon: <Grain /> },
      { label: 'Potato', icon: <Grain /> },
      { label: 'Noodle', icon: <Grain /> },
      { label: 'Flour', icon: <Grain /> },
      { label: 'Couscous', icon: <Grain /> },
      { label: 'Barley', icon: <Grain /> },
      { label: 'Bulgur', icon: <Grain /> },
      { label: 'Farro', icon: <Grain /> },
      { label: 'Brown Rice', icon: <Grain /> },
      { label: 'Wild Rice', icon: <Grain /> },
      { label: 'Tortilla', icon: <Grain /> },
      { label: 'Polenta', icon: <Grain /> },
      { label: 'Basmati Rice', icon: <Grain /> },
      { label: 'Jasmine Rice', icon: <Grain /> },
    ],
  },
  {
    name: 'dietaryPreferences',
    title: 'Dietary Preferences',
    required: false,
    chips: [
      { label: 'Vegetarian', icon: <HealthAndSafety /> },
      { label: 'Vegan', icon: <HealthAndSafety /> },
      { label: 'Gluten-Free', icon: <HealthAndSafety /> },
      { label: 'Keto', icon: <HealthAndSafety /> },
      { label: 'Paleo', icon: <HealthAndSafety /> },
      { label: 'Dairy-Free', icon: <HealthAndSafety /> },
      { label: 'Nut-Free', icon: <HealthAndSafety /> },
      { label: 'Halal', icon: <HealthAndSafety /> },
      { label: 'Kosher', icon: <HealthAndSafety /> },
      { label: 'Pescatarian', icon: <HealthAndSafety /> },
    ],
  },
  {
    name: 'cookingMethods',
    title: 'Cooking Methods',
    required: false,
    chips: [
      { label: 'Bake', icon: <Whatshot /> },
      { label: 'Grill', icon: <Whatshot /> },
      { label: 'Fry', icon: <Whatshot /> },
      { label: 'Boil', icon: <Whatshot /> },
      { label: 'Steam', icon: <Whatshot /> },
      { label: 'Roast', icon: <Whatshot /> },
      { label: 'Stir-Fry', icon: <Whatshot /> },
      { label: 'Slow Cook', icon: <Whatshot /> },
      { label: 'Sauté', icon: <Whatshot /> },
      { label: 'Broil', icon: <Whatshot /> },
      { label: 'Poach', icon: <Whatshot /> },
      { label: 'Braise', icon: <Whatshot /> },
      { label: 'Pressure Cook', icon: <Whatshot /> },
      { label: 'Air Fry', icon: <Whatshot /> },
      { label: 'Smoke', icon: <Whatshot /> },
      { label: 'Sear', icon: <Whatshot /> },
    ],
  },
  {
    name: 'additionalModifiers',
    title: 'Additional Modifiers',
    required: false,
    chips: [
      { label: 'Spicy', icon: <Star /> },
      { label: 'Sweet', icon: <Star /> },
      { label: 'Salty', icon: <Star /> },
      { label: 'Healthy', icon: <Star /> },
      { label: 'Quick', icon: <Star /> },
      { label: 'Easy', icon: <Star /> },
      { label: 'Gourmet', icon: <Star /> },
      { label: 'Traditional', icon: <Star /> },
      { label: 'Savory', icon: <Star /> },
      { label: 'Creamy', icon: <Star /> },
      { label: 'Crunchy', icon: <Star /> },
      { label: 'Fresh', icon: <Star /> },
      { label: 'Comforting', icon: <Star /> },
      { label: 'Bold', icon: <Star /> },
      { label: 'Zesty', icon: <Star /> },
      { label: 'Smoky', icon: <Star /> },
      { label: 'Hearty', icon: <Star /> },
      { label: 'Light', icon: <Star /> },
    ],
  },
];

const Composer: React.FC = () => {
  const navigate = useNavigate();
  const { selectedCategories, addChip, removeChip, buildPrompt, prompt, setResults, clearAll, mode } = useRecipe();
  const { userId } = useAuth();
  const [expanded, setExpanded] = useState<string | false>(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [localSelectedCategories, setLocalSelectedCategories] = useState(selectedCategories);

  useEffect(() => {
    buildPrompt();
  }, [selectedCategories, buildPrompt]);

  useEffect(() => {
    setLoading(false);
  }, []);

  // Sync local state with context state
  useEffect(() => {
    setLocalSelectedCategories(selectedCategories);
  }, [selectedCategories]);

  const isSelected = (category: string, label: string) => {
    return localSelectedCategories[category as keyof typeof localSelectedCategories].includes(label);
  };

  const handleChipClick = (category: string, label: string) => {
    const categoryKey = category as keyof typeof selectedCategories;
    
    if (category === 'mealType' || category === 'cuisineStyle') {
      if (isSelected(category, label)) {
        // Remove the chip
        const newCategories = {
          ...localSelectedCategories,
          [categoryKey]: localSelectedCategories[categoryKey].filter((chip) => chip !== label),
        };
        setLocalSelectedCategories(newCategories);
        removeChip(categoryKey, label);
      } else {
        // Clear all in category and add this one
        const newCategories = {
          ...localSelectedCategories,
          [categoryKey]: [label],
        };
        setLocalSelectedCategories(newCategories);
        
        // Clear all chips in context and add the new one
        localSelectedCategories[categoryKey].forEach((chip) => {
          removeChip(categoryKey, chip);
        });
        addChip(categoryKey, label);
      }
    } else {
      if (isSelected(category, label)) {
        // Remove the chip
        const newCategories = {
          ...localSelectedCategories,
          [categoryKey]: localSelectedCategories[categoryKey].filter((chip) => chip !== label),
        };
        setLocalSelectedCategories(newCategories);
        removeChip(categoryKey, label);
      } else {
        // Add the chip
        const newCategories = {
          ...localSelectedCategories,
          [categoryKey]: [...localSelectedCategories[categoryKey], label],
        };
        setLocalSelectedCategories(newCategories);
        addChip(categoryKey, label);
      }
    }
  };

  const handleRemoveSelectedChip = (category: string, label: string) => {
    const categoryKey = category as keyof typeof selectedCategories;
    const newCategories = {
      ...localSelectedCategories,
      [categoryKey]: localSelectedCategories[categoryKey].filter((chip) => chip !== label),
    };
    setLocalSelectedCategories(newCategories);
    removeChip(categoryKey, label);
  };

  const handleGenerate = async () => {
    if (!isValid) {
      setSnackbarMessage('Please select Meal Type and at least one Main Ingredient or Vegetable/Fruit');
      setSnackbarOpen(true);
      // Auto-expand first missing category
      if (selectedCategories.mealType.length === 0) {
        setExpanded('mealType');
      } else if (selectedCategories.mainIngredients.length === 0 && selectedCategories.vegetablesFruits.length === 0) {
        setExpanded('mainIngredients');
      }
      return;
    }
    setGenerating(true);
    try {
      const results = await mockGenerateRecipes({ selections: selectedCategories, mode, userId });
      setResults(results);
      navigate('/results');
    } catch (error) {
      console.error('Error generating recipes:', error);
      setSnackbarMessage('Failed to generate recipes. Please try again.');
      setSnackbarOpen(true);
    } finally {
      setGenerating(false);
    }
  };

  const allSelectedChips = Object.entries(selectedCategories).flatMap(([category, chips]) =>
    chips.map((chip) => ({ category, label: chip }))
  );

  const requiredCategories = ['mealType', 'mainIngredients', 'vegetablesFruits'];
  const completedRequired = requiredCategories.filter(cat => selectedCategories[cat as keyof typeof selectedCategories].length > 0).length;
  const totalRequired = mode === 'find' ? 3 : 3; // Same for both modes since we still require the same categories
  const completion = (completedRequired / totalRequired) * 100;

  const isValid = selectedCategories.mealType.length > 0 && (selectedCategories.mainIngredients.length > 0 || selectedCategories.vegetablesFruits.length > 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', maxWidth: '1200px', mx: 'auto' }}>
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton edge="start" sx={{ color: 'white' }} onClick={() => navigate('/')} aria-label="Go back to landing page">
              <ArrowBack />
            </IconButton>
            <Typography variant="body2" sx={{ ml: 1, fontSize: { xs: '1rem', md: '1.25rem' }, fontWeight: 'bold', color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.3)', cursor: 'pointer', '&:hover': { opacity: 0.8 } }} onClick={() => navigate('/')}>
              Home
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'center', fontSize: { xs: '1rem', md: '1.25rem' }, fontWeight: 'bold', color: 'white', textShadow: '1px 1px 2px rgba(0,0,0,0.3)', display: { xs: 'none', md: 'block' } }}>
            {mode === 'find' ? 'Find a Recipe' : 'Build Your Recipe'}
          </Typography>
          <Tooltip title={!isValid ? "Complete required selections to continue" : generating ? "Creating your recipe..." : ""}>
            <Typography
                variant="body2"
                sx={{
                  ml: 2,
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  fontWeight: 'bold',
                  color: generating ? 'warning.main' : 'white',
                  textShadow: generating ? 'none' : '1px 1px 2px rgba(0,0,0,0.3)',
                  cursor: !isValid || generating ? 'not-allowed' : 'pointer',
                  opacity: !isValid && !generating ? 0.5 : 1,
                  '&:hover': !isValid || generating ? {} : { opacity: 0.8 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
                onClick={!isValid || generating ? undefined : handleGenerate}
              >
                {generating ? (mode === 'find' ? 'Finding...' : 'Creating...') : (mode === 'find' ? 'Find Recipes' : 'Continue')}
                {generating ? <CircularProgress size={16} color="warning" sx={{ ml: 0.5 }} /> : <ArrowForward />}
              </Typography>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'background.paper' }}>
        <LinearProgress variant="determinate" value={completion} sx={{ height: 8 }} />
      </Box>
      {allSelectedChips.length > 0 && (
        <Box
          sx={{
            position: 'sticky',
            top: 8, // Below progress bar
            zIndex: 1,
            backgroundColor: 'background.paper',
            p: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {completedRequired} / {totalRequired} required selected
            </Typography>
            <Button size="small" onClick={clearAll} variant="outlined" aria-label="Clear all selected chips">
              Clear All
            </Button>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {allSelectedChips.map(({ category, label }) => (
              <Chip
                key={`${category}-${label}`}
                label={label}
                onDelete={() => handleRemoveSelectedChip(category, label)}
                size="small"
                sx={{ fontSize: { xs: '0.7rem', md: '0.8rem' } }}
                deleteIcon={<ClearIcon aria-label={`Remove ${label} from selection`} />}
              />
            ))}
          </Stack>
        </Box>
      )}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: { xs: 1, md: 2 } }}>
        <Stack spacing={2}>
          {categories
            .filter(category => {
              if (mode === 'find') {
                return ['mealType', 'cuisineStyle', 'mainIngredients', 'vegetablesFruits', 'dietaryPreferences'].includes(category.name);
              }
              return true;
            })
            .filter(category => {
              // Hide Additional Modifiers on mobile
              if (category.name === 'additionalModifiers') {
                return false; // Will conditionally render below
              }
              return true;
            })
            .map((category) => (
            <Accordion
              key={category.name}
              expanded={expanded === category.name}
              onChange={(event, isExpanded) => {
                setExpanded(isExpanded ? category.name : false);
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMore />}
                sx={{
                  borderLeft: category.required && selectedCategories[category.name as keyof typeof selectedCategories].length === 0 && !(category.name === 'vegetablesFruits' && selectedCategories.mainIngredients.length > 0) && !(category.name === 'mainIngredients' && selectedCategories.vegetablesFruits.length > 0) ? '4px solid red' : 'none',
                  fontWeight: category.required && selectedCategories[category.name as keyof typeof selectedCategories].length === 0 && !(category.name === 'vegetablesFruits' && selectedCategories.mainIngredients.length > 0) && !(category.name === 'mainIngredients' && selectedCategories.vegetablesFruits.length > 0) ? 'bold' : 'normal',
                }}
              >
                <Typography sx={{ flexGrow: 1, fontSize: { xs: '1rem', md: '1.1rem' } }}>
                  {category.title}{category.required && !(category.name === 'vegetablesFruits' && selectedCategories.mainIngredients.length > 0) && !(category.name === 'mainIngredients' && selectedCategories.vegetablesFruits.length > 0) ? <span style={{ color: 'red' }}>*</span> : ''}
                </Typography>
                {selectedCategories[category.name as keyof typeof selectedCategories].length > 0 && (
                  <CheckIcon sx={{ mr: 1, color: 'success.main' }} />
                )}
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ 
                  overflowX: 'visible', 
                  pb: 1, 
                  '&::-webkit-scrollbar': { height: '4px' }, 
                  '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '2px' },
                  minHeight: category.name === 'cuisineStyle' ? '60px' : 'auto'
                }}>
                  {loading ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <Skeleton key={i} variant="rectangular" width={80} height={32} />
                      ))}
                    </Stack>
                  ) : (
                    <Stack 
                      direction="row" 
                      spacing={1} 
                      sx={{ 
                        flexWrap: 'wrap'
                      }}
                    >
                      {category.chips.map((chip) => (
                        <Chip
                          key={chip.label}
                          icon={chip.icon}
                          label={chip.label}
                          variant={isSelected(category.name, chip.label) ? 'filled' : 'outlined'}
                          onClick={() => handleChipClick(category.name, chip.label)}
                          onDelete={isSelected(category.name, chip.label) ? () => handleRemoveSelectedChip(category.name, chip.label) : undefined}
                          sx={{ 
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                            ...(isSelected(category.name, chip.label) && {
                              backgroundColor: 'primary.main',
                              color: 'white',
                              '& .MuiChip-deleteIcon': {
                                color: 'white',
                              },
                            })
                          }}
                          aria-label={`Toggle ${chip.label} selection`}
                          deleteIcon={isSelected(category.name, chip.label) ? <ClearIcon aria-label={`Remove ${chip.label} from selection`} /> : undefined}
                          disabled={false}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
                {(category.name === 'mainIngredients' || category.name === 'vegetablesFruits') && selectedCategories[category.name as keyof typeof selectedCategories].length === 0 && selectedCategories[(category.name === 'mainIngredients' ? 'vegetablesFruits' : 'mainIngredients') as keyof typeof selectedCategories].length === 0 && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    Select at least one
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
          {/* Additional Modifiers - hidden on mobile */}
          {categories
            .filter(category => category.name === 'additionalModifiers')
            .map((category) => (
            <Accordion
              key={category.name}
              expanded={expanded === category.name}
              onChange={(event, isExpanded) => {
                setExpanded(isExpanded ? category.name : false);
              }}
              sx={{ display: { xs: 'none', md: 'block' } }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMore />}
                sx={{
                  borderLeft: category.required && selectedCategories[category.name as keyof typeof selectedCategories].length === 0 && !(category.name === 'vegetablesFruits' && selectedCategories.mainIngredients.length > 0) && !(category.name === 'mainIngredients' && selectedCategories.vegetablesFruits.length > 0) ? '4px solid red' : 'none',
                  fontWeight: category.required && selectedCategories[category.name as keyof typeof selectedCategories].length === 0 && !(category.name === 'vegetablesFruits' && selectedCategories.mainIngredients.length > 0) && !(category.name === 'mainIngredients' && selectedCategories.vegetablesFruits.length > 0) ? 'bold' : 'normal',
                }}
              >
                <Typography sx={{ flexGrow: 1, fontSize: { xs: '1rem', md: '1.1rem' } }}>
                  {category.title}{category.required && !(category.name === 'vegetablesFruits' && selectedCategories.mainIngredients.length > 0) && !(category.name === 'mainIngredients' && selectedCategories.vegetablesFruits.length > 0) ? <span style={{ color: 'red' }}>*</span> : ''}
                </Typography>
                {selectedCategories[category.name as keyof typeof selectedCategories].length > 0 && (
                  <CheckIcon sx={{ mr: 1, color: 'success.main' }} />
                )}
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ 
                  overflowX: 'visible', 
                  pb: 1, 
                  '&::-webkit-scrollbar': { height: '4px' }, 
                  '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '2px' },
                  minHeight: category.name === 'cuisineStyle' ? '60px' : 'auto'
                }}>
                  {loading ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <Skeleton key={i} variant="rectangular" width={80} height={32} />
                      ))}
                    </Stack>
                  ) : (
                    <Stack 
                      direction="row" 
                      spacing={1} 
                      sx={{ 
                        flexWrap: 'wrap'
                      }}
                    >
                      {category.chips.map((chip) => (
                        <Chip
                          key={chip.label}
                          icon={chip.icon}
                          label={chip.label}
                          variant={isSelected(category.name, chip.label) ? 'filled' : 'outlined'}
                          onClick={() => handleChipClick(category.name, chip.label)}
                          onDelete={isSelected(category.name, chip.label) ? () => handleRemoveSelectedChip(category.name, chip.label) : undefined}
                          sx={{ 
                            fontSize: { xs: '0.75rem', md: '0.875rem' },
                            ...(isSelected(category.name, chip.label) && {
                              backgroundColor: 'primary.main',
                              color: 'white',
                              '& .MuiChip-deleteIcon': {
                                color: 'white',
                              },
                            })
                          }}
                          aria-label={`Toggle ${chip.label} selection`}
                          deleteIcon={isSelected(category.name, chip.label) ? <ClearIcon aria-label={`Remove ${chip.label} from selection`} /> : undefined}
                          disabled={false}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
                {(category.name === 'mainIngredients' || category.name === 'vegetablesFruits') && selectedCategories[category.name as keyof typeof selectedCategories].length === 0 && selectedCategories[(category.name === 'mainIngredients' ? 'vegetablesFruits' : 'mainIngredients') as keyof typeof selectedCategories].length === 0 && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    Select at least one
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
        {prompt && (
          <Card sx={{ mt: 2 }}>
            <CardContent sx={{ py: 1 }}>
              <Typography variant="h6">
                <span style={{ fontSize: '1.375rem', fontWeight: 'normal', fontFamily: "'Dancing Script', cursive", color: '#2e7d32' }}>{prompt}</span>
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity="error" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Composer;